import { DataFileInterface } from '@systemeio/file-manager'
import { DangerButton, SuccessButton } from '@systemeio/ui'
import MobileVariantsTable from 'modules/products/components/Variants/mobile-variants-table'
import Variant from 'modules/products/components/Variants/variant'
import {
  ProductVariantInterface,
  ProductVariantsErrorsInterface,
} from 'modules/products/types/product-variant-interface'
import { getFilteredVariantsImagesList } from 'modules/products/utils/product-variants-utils'
import { useEffect, useState } from 'react'
import { useLocoTranslation } from 'shared/hooks/use-loco-translation'
import useMediaQuery from 'shared/hooks/use-media-query'
import useWindowSize from 'shared/hooks/use-window-size'
import { twMerge } from 'tailwind-merge'
import { ulid } from 'ulid'

interface VariantsProps {
  allowStock?: boolean
  stock: number
  variants: ProductVariantInterface[]
  setVariants: React.Dispatch<React.SetStateAction<ProductVariantInterface[]>>
  errors: ProductVariantsErrorsInterface
  setErrors: React.Dispatch<React.SetStateAction<ProductVariantsErrorsInterface>>
}

function Variants({ allowStock, stock, variants, setVariants, errors, setErrors }: VariantsProps) {
  const [selectedVariants, setSelectedVariants] = useState<ProductVariantInterface[]>([])
  const [isDeleteAvailable, setIsDeleteAvailable] = useState(true)
  const [variantsImages, setVariantsImages] = useState<DataFileInterface[]>([])
  const { t } = useLocoTranslation()
  const { width } = useWindowSize()
  const { isLG } = useMediaQuery()

  const handleMarkVariantAsDeleted = () => {
    setVariants(prev =>
      prev.map(v => (selectedVariants.includes(v) ? { ...v, deleted: true, selected: false } : v)),
    )
  }

  const handleUnmarkVariantAsDeleted = () => {
    setVariants(prev =>
      prev.map(v =>
        selectedVariants.includes(v) ? { ...v, id: ulid(), deleted: false, selected: false } : v,
      ),
    )
  }

  useEffect(() => {
    const filteredSelectedVariants = variants.filter(variant => variant.selected)

    if (filteredSelectedVariants.length === 1) {
      if (filteredSelectedVariants[0].deleted) {
        setIsDeleteAvailable(false)
      } else {
        setIsDeleteAvailable(true)
      }
    }
    setVariantsImages(getFilteredVariantsImagesList(variants))
    setSelectedVariants(filteredSelectedVariants)
  }, [variants])

  useEffect(() => {
    if (allowStock && stock > 0) {
      setVariants(prev => prev.map(v => ({ ...v, stock: v.stock ?? stock })))
    }
  }, [stock, allowStock])

  const hasStock = allowStock || variants?.some(v => v.stock && v.stock > 0)

  return (
    <>
      {!!selectedVariants.length && (
        <div className="my-2 inline-flex text-base">
          <div className="text-text-primary self-center">
            {t('dashboard.products.variants.selected.title', { count: selectedVariants.length })}
          </div>
          {isDeleteAvailable ? (
            <DangerButton onClick={handleMarkVariantAsDeleted} className="ml-2 !py-0">
              {t('dashboard.products.delete_variants.title', {
                count: selectedVariants.length,
              })}
            </DangerButton>
          ) : (
            <SuccessButton onClick={handleUnmarkVariantAsDeleted} className="ml-2 !py-0">
              {t('dashboard.products.restore_variants.title', {
                count: selectedVariants.length,
              })}
            </SuccessButton>
          )}
        </div>
      )}
      {!isLG ? (
        <MobileVariantsTable
          variants={variants}
          setVariants={setVariants}
          isDeleteAvailable={!!selectedVariants.length ? isDeleteAvailable : null}
          variantsImages={variantsImages}
        />
      ) : (
        <div className="grid">
          <div
            className={twMerge(
              'grid justify-items-center',
              hasStock
                ? 'grid-cols-[18px_56px_minmax(15%,30%)_minmax(10%,18%)_minmax(10%,18%)_minmax(10%,20%)] gap-2'
                : 'grid-cols-[25px_70px_minmax(15%,45%)_minmax(10%,20%)_minmax(10%,20%)] gap-5',
            )}
          >
            <div className="col-start-3 justify-self-start">
              {t('dashboard.products.variant.title')}
            </div>
            <div className="col-start-4 justify-self-center">
              {t('dashboard.products.sku.title')}
            </div>
            <div className="col-start-5 justify-self-center">{t('global.price')}</div>
            {hasStock && (
              <div className="col-start-6 justify-self-center">
                {t('dashboard.products.stock.header')}
              </div>
            )}
          </div>
          <hr className="border-t-1 border-text-secondary mb-2 mt-2 border-solid lg:-mx-6 lg:-mr-6" />
          {variants.map(variant => (
            <Variant
              allowStock={allowStock}
              key={variant.id}
              variant={variant}
              setVariants={setVariants}
              isDeleteAvailable={!!selectedVariants.length ? isDeleteAvailable : null}
              variantsImages={variantsImages}
              errors={
                Object.keys(errors).includes(variant.id)
                  ? errors[variant.id]
                  : { sku: '', stock: '', price: '' }
              }
              setErrors={setErrors}
            />
          ))}
        </div>
      )}
    </>
  )
}
export default Variants
